import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import { Textarea } from "@/v5/components/ui/textarea";
import { Loader2, StickyNote } from "lucide-react";
import { useCallback, useId, useMemo, useState } from "react";
import { toast } from "@/store/toastStore";
import { RecipientPicker } from "@/v5/components/RecipientPicker";
import { MentionSuggestions, optionDomId } from "@/v5/components/mentions/MentionSuggestions";
import { useMentionAutocomplete } from "@/v5/components/mentions/useMentionAutocomplete";

interface NoteReplyTo {
  replyID: string;
  preview: string;
}

interface NoteComposeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientName: string;
  planId?: string | number | null;
  onSend: (content: string, replyID?: string, recipients?: string[], subject?: string) => Promise<void>;
  replyTo?: NoteReplyTo;
}

export function NoteComposeModal({
  open,
  onOpenChange,
  patientName,
  planId,
  onSend,
  replyTo,
}: NoteComposeModalProps) {
  const [content, setContent] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  // Tags picked in the dialog. Tags typed as "@Name" are held by the mention
  // hook instead, because those are tied to text that can be edited away.
  const [pickerTokens, setPickerTokens] = useState<string[]>([]);
  const listboxId = useId();

  const mentions = useMentionAutocomplete({
    value: content,
    setValue: setContent,
    planId,
    disabled: sending,
  });

  // What actually gets sent. Someone named in the body and also ticked in the
  // dialog is one tag, not two — the server dedupes as well, but the chips
  // above the composer should not show them twice either.
  const recipients = useMemo(
    () => Array.from(new Set([...pickerTokens, ...mentions.tokens])),
    [pickerTokens, mentions.tokens],
  );

  // Removing a chip has to forget the tag wherever it came from.
  const handleRecipientsChange = useCallback(
    (next: string[]) => {
      const keep = new Set(next);
      for (const token of mentions.tokens) {
        if (!keep.has(token)) mentions.removeToken(token);
      }
      setPickerTokens(next.filter((t) => !mentions.tokens.includes(t)));
    },
    [mentions],
  );

  const reset = useCallback(() => {
    setContent("");
    setSubject("");
    setPickerTokens([]);
    mentions.reset();
    setSending(false);
  }, [mentions]);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!v) reset();
      onOpenChange(v);
    },
    [onOpenChange, reset],
  );

  const handleSend = useCallback(async () => {
    if (!content.trim()) {
      toast.error("Note content is required");
      return;
    }
    setSending(true);
    try {
      await onSend(content, replyTo?.replyID, recipients, subject.trim() || undefined);
      toast.success("Note added");
      handleOpenChange(false);
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSending(false);
    }
  }, [content, subject, recipients, onSend, handleOpenChange, replyTo]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg"
        // Radix watches for Escape on the document in the CAPTURE phase, so it
        // decides before the key ever reaches the textarea. Dismissing the name
        // list has to be intercepted here or the first Escape closes the dialog
        // and resets the draft. Same shape as the IME guard in DialogContent.
        onEscapeKeyDown={(e) => {
          if (mentions.open) {
            e.preventDefault();
            mentions.close();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-indigo-500" />
            {replyTo ? "Add Linked Internal Note" : "Add Note"}
          </DialogTitle>
          <DialogDescription>
            {replyTo
              ? `Add an internal note linked to the selected entry on ${patientName}\u2019s record. This is not sent to the patient.`
              : `Add an internal note to ${patientName}\u2019s record`}
          </DialogDescription>
        </DialogHeader>

        {replyTo && (
          <div className="rounded-lg border border-border bg-muted/60 px-3 py-2">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Related entry</p>
            <div className="flex items-start gap-2">
              <StickyNote className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <p className="line-clamp-3 text-xs text-muted-foreground">{replyTo.preview}</p>
            </div>
          </div>
        )}

        <RecipientPicker
          value={recipients}
          onChange={handleRecipientsChange}
          planId={planId}
          disabled={sending}
        />

        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject (optional)"
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />

        <div className="relative">
          <Textarea
            value={content}
            onChange={mentions.handleChange}
            onKeyDown={mentions.handleKeyDown}
            onKeyUp={mentions.handleCaretMove}
            onClick={mentions.handleCaretMove}
            placeholder="Type your note, or @ someone to notify them..."
            className="min-h-[180px] resize-y"
            aria-expanded={mentions.open}
            aria-controls={mentions.open ? listboxId : undefined}
            aria-activedescendant={
              mentions.open ? optionDomId(listboxId, mentions.activeIndex) : undefined
            }
            autoFocus
          />
          <MentionSuggestions
            open={mentions.open}
            sections={mentions.sections}
            options={mentions.options}
            activeIndex={mentions.activeIndex}
            onHover={mentions.setActiveIndex}
            onSelect={mentions.select}
            idPrefix={listboxId}
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !content.trim()}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <StickyNote className="h-4 w-4" />
            )}
            Save Note
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
